import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, Download, Share2, Loader2, Film } from "lucide-react";
import apiClient, { API, SERVICES_META } from "@/lib/api";
import { toast } from "sonner";

export default function EventGallery() {
  const { galleryId } = useParams();
  const [gallery, setGallery] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound

  useEffect(() => {
    apiClient
      .get(`/gallery/${galleryId}`)
      .then(({ data }) => {
        setGallery(data);
        setStatus("ok");
      })
      .catch(() => setStatus("notfound"));
  }, [galleryId]);

  const share = async (m) => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({
          title: "Luxury Kingz — Event Gallery",
          text: "Check out our moments from the Luxury Kingz booth! Download your clip and share it.",
          url,
        });
        return;
      }
    } catch (e) {
      if (e?.name === "AbortError") return;
    }
    navigator.clipboard.writeText(url);
    toast.success("Gallery link copied — paste it anywhere!");
  };

  return (
    <div className="bg-[#050505] min-h-screen text-white" data-testid="event-gallery-page">
      <header className="border-b border-[#D4AF37]/15 px-6 md:px-12 py-5 flex items-center justify-between backdrop-blur-xl bg-[#050505]/80 sticky top-0 z-40">
        <Link to="/" className="flex items-center gap-3">
          <Crown className="w-6 h-6 text-[#D4AF37]" />
          <span className="font-serif text-xl">
            <span className="text-white">Luxury</span>{" "}
            <span className="gold-gradient-text font-medium">Kingz</span>
          </span>
        </Link>
        <Link to="/book" className="lk-btn-primary text-xs" data-testid="gallery-book-cta">
          Book Your Event
        </Link>
      </header>

      {status === "loading" && (
        <div className="flex items-center justify-center py-48">
          <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
        </div>
      )}

      {status === "notfound" && (
        <div className="text-center py-48 px-6" data-testid="gallery-not-found">
          <Film className="w-10 h-10 text-zinc-600 mx-auto mb-5" />
          <h1 className="font-serif text-3xl mb-3">Gallery not found</h1>
          <p className="text-zinc-500 text-sm">This link may have expired. Contact your event host.</p>
        </div>
      )}

      {status === "ok" && gallery && (
        <section className="px-6 md:px-12 lg:px-24 py-16">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <span className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">
              {SERVICES_META[gallery.service]?.title || "Event Gallery"} · {gallery.event_date}
            </span>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light mt-3 mb-4" data-testid="gallery-title">
              {gallery.host ? `${gallery.host}'s ` : ""}
              <span className="italic gold-gradient-text">{gallery.event_type}.</span>
            </h1>
            <p className="text-zinc-400 max-w-xl leading-relaxed mb-12">
              Relive the night. Tap download to save your clip, then share it straight to Instagram or TikTok
              from your phone's share sheet. Tag <span className="text-[#D4AF37]">@LuxuryKingzLLC</span> — we'd love to see it!
            </p>
          </motion.div>

          {gallery.media.length === 0 ? (
            <div className="lk-card p-16 text-center text-zinc-500" data-testid="gallery-empty">
              Your host is still uploading moments — check back soon!
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {gallery.media.map((m, i) => (
                <motion.div
                  key={m.id}
                  className="border border-[#D4AF37]/15 bg-black/50 group"
                  data-testid={`gallery-media-${i}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
                >
                  {m.kind === "video" ? (
                    <video
                      src={`${API}/gallery/${gallery.id}/media/${m.id}`}
                      controls
                      playsInline
                      preload="metadata"
                      className="w-full aspect-[4/3] object-cover bg-black"
                    />
                  ) : (
                    <img
                      src={`${API}/gallery/${gallery.id}/media/${m.id}`}
                      alt={m.original_filename}
                      className="w-full aspect-[4/3] object-cover"
                    />
                  )}
                  <div className="p-4 flex items-center justify-between gap-3">
                    <a
                      href={`${API}/gallery/${gallery.id}/media/${m.id}?download=1`}
                      className="text-xs uppercase tracking-widest text-[#D4AF37] hover:text-[#F3E5AB] inline-flex items-center gap-2"
                      data-testid={`download-media-${i}`}
                    >
                      <Download className="w-4 h-4" /> Download
                    </a>
                    <button
                      onClick={() => share(m)}
                      className="text-xs uppercase tracking-widest text-zinc-400 hover:text-[#D4AF37] inline-flex items-center gap-2"
                      data-testid={`share-media-${i}`}
                    >
                      <Share2 className="w-4 h-4" /> Share
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          <div className="mt-20 text-center border-t border-[#D4AF37]/10 pt-14">
            <h2 className="font-serif text-3xl mb-3">
              Want this at <span className="italic gold-gradient-text">your</span> event?
            </h2>
            <p className="text-zinc-400 text-sm mb-7">Photo booth & 360° experiences from $350.</p>
            <Link to="/book" className="lk-btn-primary text-xs" data-testid="gallery-footer-cta">
              Reserve Your Date
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
